import api, { generateUUID } from '../../../shared/utils/api';

export interface AgentChatRequest {
  message: string;
  session_id?: string;
}

export interface AgentChatResponse {
  reply: string;
  provider: string;
}

/**
 * One tool-call step emitted by the V2 LangGraph agent, mirroring the
 * `_extract_tool_steps` shape in
 * janus-tentant-agent-ai/app/services/agent_service.py.
 *
 * `ok === null` means the tool call has no matching ToolMessage yet
 * (should not happen in the current single-event batch, but kept
 * permissive for future token-level streaming where tool_start events
 * may arrive before tool_end).
 */
export interface AgentToolStep {
  id: string;
  name: string;
  args: Record<string, unknown>;
  ok: boolean | null;
  summary: string | null;
}

/**
 * Optional structured payload carried by the terminal `done` SSE event.
 *
 * The portal/data agent (janus-tentant-agent-ai) MAY enrich the `done`
 * envelope with extra metadata. Today only `handoff_to_assistant` is
 * consumed by the floating Janus Agent widget — when the data agent
 * decides a question is really about the platform (how-to / billing /
 * settings) it can set this flag so the UI surfaces a hand-off to the
 * Support Assistant. The field is OPTIONAL and absence is the norm;
 * every consumer must tolerate it being undefined.
 */
export interface AgentDonePayload {
  handoff_to_assistant?: boolean;
  [key: string]: unknown;
}

export interface AgentStreamCallbacks {
  onToken: (token: string) => void;
  onToolStep?: (step: AgentToolStep) => void;
  /**
   * Called once when the stream finishes. Receives the raw `done` event
   * payload when the server sent the type-tagged envelope, so callers can
   * read optional fields like `handoff_to_assistant`. The argument is
   * optional — legacy callers that declare `onDone: () => void` stay valid.
   */
  onDone: (payload?: AgentDonePayload) => void;
  onError: (err: Error) => void;
}

export const agentApi = {
  chat: async (request: AgentChatRequest): Promise<AgentChatResponse> => {
    const { data } = await api.post<AgentChatResponse>('/agent/chat', request);
    return data;
  },

  chatStream(
    request: AgentChatRequest,
    callbacks: AgentStreamCallbacks,
  ): AbortController {
    const { onToken, onToolStep, onDone, onError } = callbacks;
    const controller = new AbortController();
    const token = localStorage.getItem('accessToken');
    const correlationId = generateUUID();

    fetch('/api/v1/agent/chat/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Correlation-ID': correlationId,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice(6).trim();
            if (!jsonStr) continue;
            try {
              const event = JSON.parse(jsonStr);
              // New type-tagged envelope (see
              // janus-tentant-agent-ai/app/routers/agent.py).
              // Fall back to the legacy flat shape for older servers
              // still deployed mid-rollout.
              const t = event.type;
              if (t === 'token') {
                onToken(String(event.content ?? ''));
              } else if (t === 'tool_step') {
                if (onToolStep && event.step) {
                  onToolStep(event.step as AgentToolStep);
                }
              } else if (t === 'done') {
                onDone(event as AgentDonePayload);
              } else if (t === 'error') {
                onError(new Error(String(event.error ?? 'stream error')));
              } else if (event.token) {
                onToken(String(event.token));
              } else if (event.done) {
                onDone();
              } else if (event.error) {
                onError(new Error(String(event.error)));
              }
            } catch {
              /* ignore parse errors */
            }
          }
        }
        onDone();
      })
      .catch((err) => {
        if (err.name !== 'AbortError') onError(err);
      });

    return controller;
  },
};
